import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { createDemandClient } from '../../agency/demandApi';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createCampaignsClient } from './api';
import type { CampaignPayload, CampaignsFilter, LineItemsFilter } from './model';

export const campaignsQueryKey = ['campaigns', 'campaign-list'] as const;
export const campaignsListsQueryKey = [...campaignsQueryKey, 'list'] as const;
const lineItemsQueryKey = ['campaigns', 'line-items'] as const;

function useCampaignsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const scope = useAgencyCampaignContext();
  if (!session) throw new Error('Shigo session is required.');
  if (scope)
    return createDemandClient({
      config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
      accessToken: session.accessToken,
      agencyId: scope.agency.id,
    }).campaigns;
  return createCampaignsClient({
    config: { apiBaseUrl: config.madBuyServiceUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useCampaignsQuery(filter: CampaignsFilter) {
  const client = useCampaignsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    enabled: !filter.agencyIds?.length,
    queryKey: [...prefix, ...campaignsListsQueryKey, filter],
    queryFn: filter.agencyIds?.length ? skipToken : () => client.listCampaigns(filter),
  });
}

export function useCampaignQuery(id: string) {
  const client = useCampaignsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    queryKey: [...prefix, ...campaignsQueryKey, 'detail', id],
    queryFn: () => client.getCampaign(id),
    enabled: Boolean(id),
  });
}

export function useLineItemsQuery(filter: LineItemsFilter) {
  const client = useCampaignsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    queryKey: [...prefix, ...lineItemsQueryKey, filter],
    queryFn: filter.agencyIds?.length ? skipToken : () => client.listLineItems(filter),
    enabled: (Boolean(scope) || Boolean(filter.campaignId)) && !filter.agencyIds?.length,
  });
}

export function useCampaignMutations() {
  const client = useCampaignsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  const queryClient = useQueryClient();
  const invalidateLists = () => {
    void queryClient.invalidateQueries({
      queryKey: [...prefix, ...campaignsListsQueryKey],
    });
  };
  return {
    create: useMutation({ mutationFn: client.createCampaign, onSuccess: invalidateLists }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: CampaignPayload }) =>
        client.updateCampaign(id, payload),
      onSuccess: (campaign) => {
        queryClient.setQueryData(
          [...prefix, ...campaignsQueryKey, 'detail', campaign.id],
          campaign,
        );
        invalidateLists();
      },
    }),
    duplicate: useMutation({
      mutationFn: ({ id, deepCopyCreatives }: { id: string; deepCopyCreatives: boolean }) =>
        client.duplicateCampaign(id, deepCopyCreatives),
      onSuccess: invalidateLists,
    }),
  };
}

export function useLineItemMutations() {
  const client = useCampaignsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  const queryClient = useQueryClient();
  return {
    duplicate: useMutation({
      mutationFn: ({ id, deepCopyCreatives }: { id: string; deepCopyCreatives: boolean }) =>
        client.duplicateLineItem(id, deepCopyCreatives),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [...prefix, ...lineItemsQueryKey] }),
    }),
  };
}
